import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../ui/select';
import { Search, Download, Eye, CheckCircle, Clock, XCircle, Calendar, DollarSign, Building2, FileX, MinusCircle } from 'lucide-react';
import { toast } from 'sonner';

const monthlyDebitNotes = {
  '2025-10': [
    {
      number: 'PDN-2024-0025',
      date: '2025-10-24',
      supplier: 'Office Supplies Co',
      relatedInvoice: 'PINV-2024-0891',
      total: 800,
      status: 'accepted',
      reason: 'Quality issue - product defects'
    },
    {
      number: 'PDN-2024-0024',
      date: '2025-10-22',
      supplier: 'Tech Equipment Ltd',
      relatedInvoice: 'PINV-2024-0885',
      total: 1200,
      status: 'pending',
      reason: 'Price discrepancy'
    }
  ]
};

export default function PurchaseDebitNotes() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('2025-10');

  const currentNotes = monthlyDebitNotes[selectedMonth as keyof typeof monthlyDebitNotes] || [];
  const filteredNotes = currentNotes.filter(note =>
    note.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    note.supplier.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    const badges = {
      accepted: <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle className="w-3 h-3 mr-1" />Accepted</Badge>,
      pending: <Badge className="bg-amber-100 text-amber-700 border-amber-200"><Clock className="w-3 h-3 mr-1" />Pending</Badge>,
      rejected: <Badge className="bg-rose-100 text-rose-700 border-rose-200"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>
    };
    return badges[status as keyof typeof badges] || <Badge>{status}</Badge>;
  };

  return (
    <div className="p-6 lg:p-8 space-y-6 bg-gradient-to-br from-slate-50 to-gray-50 min-h-screen">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg blur-md opacity-50"></div>
            <div className="relative p-2 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg premium-shadow">
              <MinusCircle className="w-6 h-6 text-white" />
            </div>
          </div>
          <div>
            <h1 className="text-3xl text-gray-900">Purchase Debit Notes</h1>
            <p className="text-gray-600 mt-1">Review and manage debit notes from suppliers</p>
          </div>
        </div>
        <Button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 shadow-lg" onClick={() => toast.success('Exported')}>
          <Download className="w-4 h-4 mr-2" />
          Export
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Debit Notes', value: currentNotes.length, icon: MinusCircle, color: 'orange' },
          { label: 'Total Amount', value: currentNotes.reduce((sum, n) => sum + n.total, 0).toLocaleString(), icon: DollarSign, color: 'red' },
          { label: 'Accepted', value: currentNotes.filter(n => n.status === 'accepted').length, icon: CheckCircle, color: 'green' },
          { label: 'Pending Review', value: currentNotes.filter(n => n.status === 'pending').length, icon: Clock, color: 'amber' }
        ].map((stat, i) => (
          <Card key={i} className={`border-l-4 border-l-${stat.color}-500 shadow-sm hover:shadow-md transition-shadow hover-lift`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                  <p className="text-3xl text-gray-900">{stat.value}</p>
                </div>
                <div className={`p-3 bg-${stat.color}-50 rounded-lg`}>
                  <stat.icon className={`w-5 h-5 text-${stat.color}-600`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="shadow-sm premium-shadow">
        <CardContent className="p-6">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input placeholder="Search debit notes..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 h-11" />
            </div>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-64 h-11">
                <Calendar className="w-4 h-4 mr-2" />
                <span>{new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2025-10">October 2025</SelectItem>
                <SelectItem value="2025-09">September 2025</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm premium-shadow hover-lift">
        <CardHeader className="border-b bg-gradient-to-r from-orange-50 to-red-50">
          <CardTitle className="flex items-center gap-2">
            <MinusCircle className="w-5 h-5 text-orange-600" />
            Purchase Debit Notes
          </CardTitle>
          <CardDescription>Showing {filteredNotes.length} of {currentNotes.length} debit notes</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Debit Note #</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Related Invoice</TableHead>
                <TableHead>Supplier</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="text-right">Amount (AED)</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredNotes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <FileX className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No debit notes found</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredNotes.map((note) => (
                  <TableRow key={note.number} className="hover:bg-gray-50/50">
                    <TableCell className="text-orange-600">{note.number}</TableCell>
                    <TableCell>{note.date}</TableCell>
                    <TableCell><Badge variant="outline" className="bg-blue-50">{note.relatedInvoice}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        {note.supplier}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">{note.reason}</TableCell>
                    <TableCell className="text-right">{note.total.toLocaleString()}</TableCell>
                    <TableCell>{getStatusBadge(note.status)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" className="text-orange-600 hover:bg-orange-50 h-8 w-8 p-0">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
